/**
 * Custom Range Calendar
 * ปฏิทินเลือกช่วงวันที่ (ไม่ใช้ react-day-picker)
 * - UI ตามแบบเดิม, ใช้ได้บน mobile ไม่ล้น
 * - ปิดใช้ (disable) วันที่ผ่านมาแล้ว
 */

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const WEEKDAYS_TH = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isBeforeDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() < startOfDay(b).getTime();
}

function isInRange(day: Date, from: Date, to: Date): boolean {
  const t = startOfDay(day).getTime();
  const f = startOfDay(from).getTime();
  const e = startOfDay(to).getTime();
  return t >= f && t <= e;
}

export interface CustomCalendarRange {
  from: Date;
  to?: Date;
}

export interface CustomCalendarProps {
  /** ค่าที่เลือก (ช่วง) */
  selected?: CustomCalendarRange | undefined;
  /** callback เมื่อเลือกช่วงเปลี่ยน */
  onSelect?: (range: CustomCalendarRange | undefined) => void;
  /** เดือนที่แสดงเมื่อโหลดครั้งแรก */
  defaultMonth?: Date;
  className?: string;
  /** ข้อความหัว (default: ช่วงวันเป้าหมาย) */
  title?: string;
  /** แสดงข้อความ footer ใต้ปฏิทิน (วันเริ่มต้น → วันสิ้นสุด) */
  showFooter?: boolean;
  /** ปิดการเลือกวันในอดีต (default: true) ถ้า false ให้เลือกวันในอดีตได้ เช่น แก้ไขเป้าหมาย */
  disablePastDates?: boolean;
}

export function CustomCalendar({
  selected,
  onSelect,
  defaultMonth,
  className,
  showFooter = true,
  disablePastDates = true,
}: CustomCalendarProps) {
  const today = React.useMemo(() => startOfDay(new Date()), []);
  const [viewMonth, setViewMonth] = React.useState(() => {
    const d = defaultMonth ?? selected?.from ?? new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const from = selected?.from;
  const to = selected?.to ?? selected?.from;

  const prevMonth = () => {
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  };

  const monthLabel = viewMonth.toLocaleDateString('th-TH', {
    month: 'long',
    year: 'numeric',
  });

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(start.getDate() - start.getDay());

  const days: { date: Date; isCurrentMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push({
      date: d,
      isCurrentMonth: d.getMonth() === month,
    });
  }

  const handleDayClick = (date: Date) => {
    if (!onSelect) return;
    const d = startOfDay(date);
    if (disablePastDates && d.getTime() < today.getTime()) return;

    if (!from) {
      onSelect({ from: date, to: date });
      return;
    }

    const toResolved = to ?? from;
    const hasRange = !isSameDay(from, toResolved);

    if (hasRange) {
      onSelect({ from: date, to: date });
      return;
    }

    const fromTime = startOfDay(from).getTime();
    if (d.getTime() < fromTime) {
      onSelect({ from: date, to: from });
    } else {
      onSelect({ from, to: date });
    }
  };

  const footerText =
    from && to
      ? `วันเริ่มต้น: ${from.toLocaleDateString('th-TH', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })} → วันสิ้นสุด: ${to.toLocaleDateString('th-TH', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}`
      : 'เลือกช่วงวันเป้าหมาย';

  return (
    <div
      className={cn(
        'rounded-lg border border-input bg-background p-3 min-w-0 max-w-full',
        'w-full box-border',
        className
      )}
      role="application"
      aria-label="ปฏิทินเลือกช่วงวันที่"
    >
      {/* <div className="flex items-center gap-2 mb-3">
        <CalendarIcon className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium text-foreground">{title}</span>
      </div> */}

      <div className="flex items-center justify-between gap-1 w-full mb-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={prevMonth}
          aria-label="เดือนก่อน"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium select-none flex-1 text-center min-w-0 truncate">
          {monthLabel}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={nextMonth}
          aria-label="เดือนถัดไป"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 gap-0 w-full">
        {WEEKDAYS_TH.map((wd) => (
          <div
            key={wd}
            className="text-muted-foreground text-[0.8rem] font-normal py-1 text-center select-none"
          >
            {wd}
          </div>
        ))}
        {days.map(({ date, isCurrentMonth }) => {
          const isPast = isBeforeDay(date, today);
          const isPastDisabled = disablePastDates && isPast;
          const isRangeStart = from && to && isSameDay(date, from);
          const isRangeEnd = from && to && isSameDay(date, to);
          const isInSelected = from && to && isInRange(date, from, to);
          const isSingle =
            from && (!to || isSameDay(from, to)) && isSameDay(date, from);

          return (
            <div
              key={date.toISOString()}
              className="aspect-square min-w-0 flex items-center justify-center p-0.5"
            >
              <button
                type="button"
                disabled={isPastDisabled}
                onClick={() => handleDayClick(date)}
                className={cn(
                  'w-full h-full min-w-0 max-w-[2.25rem] max-h-[2.25rem] mx-auto rounded-full text-sm font-normal leading-none transition-colors',
                  'flex items-center justify-center aspect-square',
                  !isCurrentMonth && 'text-muted-foreground/60',
                  isCurrentMonth && !isPastDisabled && 'text-foreground',
                  isPastDisabled && 'text-muted-foreground/50 cursor-not-allowed',
                  isPastDisabled && 'disabled:pointer-events-none',
                  !isPastDisabled && 'hover:bg-accent hover:text-accent-foreground',
                  isSingle &&
                    'bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground',
                  isInSelected &&
                    !isRangeStart &&
                    !isRangeEnd &&
                    'bg-primary/20 text-primary-foreground rounded-full',
                  isRangeStart &&
                    'bg-primary text-primary-foreground',
                  isRangeEnd &&
                    'bg-primary text-primary-foreground'
                )}
              >
                {date.getDate()}
              </button>
            </div>
          );
        })}
      </div>

      {showFooter && (
        <p className="text-sm text-muted-foreground mt-3 text-center">
          {footerText}
        </p>
      )}
    </div>
  );
}
